import { Timer, Zap } from 'lucide-react';
import { AvailableListRefreshLoading } from '@/features/game/AvailableListRefreshLoading';
import type { FrontendTraining } from '@/features/game/trainings/trainingTypes';
import { TrainingDumbbellInCircle } from '@/features/game/trainings/TrainingDumbbellInCircle';
import { REWARD_XP_CLASS } from '@/features/game/missions/missionRewardClasses';

const AVAILABLE_TILE_BG = 'bg-[hsl(220_20%_14%)]';
const ROW_BG = 'bg-[hsl(220_18%_19%)] hover:bg-[hsl(220_18%_22%)]';
const BTN_GOLD =
  'cursor-pointer rounded-md bg-[hsl(45,88%,48%)] px-4 py-2.5 font-heading text-xs font-black uppercase tracking-[0.12em] text-black shadow-sm transition-[filter] hover:brightness-105 active:brightness-95 disabled:cursor-not-allowed disabled:opacity-40';

const TITLE_WHITE = 'font-heading text-sm font-bold uppercase tracking-tight text-white';

type Props = {
  trainings: FrontendTraining[];
  hasActiveTraining: boolean;
  currentTrainingPoints: number;
  onSelect: (training: FrontendTraining) => void;
  t: (key: string) => string;
  isLoadingNewList?: boolean;
};

export function AvailableTrainingsSection({
  trainings,
  hasActiveTraining,
  currentTrainingPoints,
  onSelect,
  t,
  isLoadingNewList = false,
}: Props) {
  return (
    <section
      className={`w-full overflow-hidden rounded-xl border border-white/[0.08] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.05),0_8px_28px_rgba(0,0,0,0.4)] transition-opacity ${AVAILABLE_TILE_BG} ${
        hasActiveTraining ? 'pointer-events-none opacity-40' : ''
      }`}
    >
      <div className="space-y-5 p-3 sm:p-5">
        <h2 className="flex items-center gap-3 font-heading text-sm font-bold uppercase tracking-[0.2em] text-white/90">
          <TrainingDumbbellInCircle size="sm" />
          {t('trainingsPage.availableTrainings')}
        </h2>
        {isLoadingNewList ? (
          <AvailableListRefreshLoading variant="trainings" message={t('trainingsPage.loadingNewTrainings')} />
        ) : null}
        {!isLoadingNewList ? (
          <div className="space-y-2.5">
            {trainings.map((row) => {
              const canAfford = currentTrainingPoints >= row.trainingPointsCost;
              const disabled = hasActiveTraining || !canAfford;
              return (
                <div
                  key={row.id}
                  className={`flex flex-col gap-3 rounded-lg border border-white/[0.06] p-3 transition-colors hover:border-[hsl(43,50%,35%)]/35 sm:flex-row sm:items-center sm:gap-3 ${ROW_BG}`}
                >
                  <div className="flex min-w-0 flex-1 items-start gap-3 sm:items-center">
                    <TrainingDumbbellInCircle />
                    <div className="min-w-0 flex-1">
                      <p className={`truncate ${TITLE_WHITE}`}>{row.name}</p>
                      <p className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                        <span className="inline-flex items-center gap-1 text-white/50">
                          <Timer className="h-3.5 w-3.5 shrink-0 text-white/40" aria-hidden />
                          {row.durationLabel}
                        </span>
                        <span className="inline-flex items-center gap-1.5 font-bold text-orange-400">
                          <Zap className="h-3.5 w-3.5 shrink-0 text-orange-400" aria-hidden />
                          <span>
                            {row.trainingPointsCost} {t('trainingsPage.trainingPointsUnit')}
                          </span>
                        </span>
                      </p>
                    </div>
                    <div className="shrink-0 space-y-0.5 text-right sm:hidden">
                      {row.skillPointsReward > 0 && row.statTranslationKey ? (
                        <p className={`${REWARD_XP_CLASS} whitespace-nowrap`}>
                          +{row.skillPointsReward} {t(row.statTranslationKey)}
                        </p>
                      ) : (
                        <p className={`${REWARD_XP_CLASS} opacity-70`}>-</p>
                      )}
                      <p className="font-heading text-[9px] font-semibold uppercase tracking-wide text-white/40">
                        {t('reward')}
                      </p>
                    </div>
                  </div>

                  <div className="hidden shrink-0 space-y-0.5 text-right sm:block">
                    {row.skillPointsReward > 0 && row.statTranslationKey ? (
                      <p className={`${REWARD_XP_CLASS} whitespace-nowrap`}>
                        +{row.skillPointsReward} {t(row.statTranslationKey)}
                      </p>
                    ) : (
                      <p className={`${REWARD_XP_CLASS} opacity-70`}>-</p>
                    )}
                    <p className="font-heading text-[10px] font-semibold uppercase tracking-wide text-white/40">
                      {t('reward')}
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => onSelect(row)}
                    disabled={disabled}
                    title={!canAfford ? t('trainingsPage.notEnoughTrainingPoints') : undefined}
                    className={`${BTN_GOLD} w-full sm:w-auto sm:shrink-0`}
                  >
                    {t('trainingsPage.beginTraining')}
                  </button>
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    </section>
  );
}
